if (!customElements.get("story-viewer")) {
  class StoryViewer extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll("[data-story-slide]"));
      this.fills = Array.from(this.querySelectorAll(".story-viewer__fill"));
      this.bars = Array.from(this.querySelectorAll(".story-viewer__bar"));
      this.frame = this.querySelector("[data-story-frame]");
      this.muteButton = this.querySelector("[data-story-mute]");
      this.pauseButton = this.querySelector("[data-story-pause]");
      this.prevButton = this.querySelector("[data-story-prev]");
      this.nextButton = this.querySelector("[data-story-next]");
      this.imageDuration = parseInt(this.getAttribute("data-image-duration"), 10) || 10000;

      this.index = 0;
      this.elapsed = 0;
      this.userPaused = false;
      this.held = false;
      this.sound = false;
      this.tick = this.tick.bind(this);
      this.onKey = this.onKey.bind(this);

      this.querySelectorAll("[data-story-close]").forEach((el) =>
        el.addEventListener("click", () => this.close())
      );
      this.prevButton.addEventListener("click", () => this.go(this.index - 1));
      this.nextButton.addEventListener("click", () => this.go(this.index + 1));
      this.pauseButton.addEventListener("click", () => {
        this.userPaused = !this.userPaused;
        this.syncPlayback();
      });
      this.muteButton.addEventListener("click", () => {
        this.sound = !this.sound;
        this.toggleAttribute("data-sound", this.sound);
        this.muteButton.setAttribute("aria-label", this.sound ? "Mute" : "Unmute");
        var video = this.currentVideo();
        if (video) video.muted = !this.sound;
      });

      this.slides.forEach((slide, i) => {
        var video = slide.querySelector("video");
        if (!video) return;
        video.addEventListener("ended", () => {
          if (i === this.index) this.go(this.index + 1);
        });
        // Unplayable video: fall back to the image timer
        video.addEventListener("error", () => slide.setAttribute("data-type", "image"), true);
      });

      this.bindPointer();
      this.bindProductDrag();
      document.addEventListener("visibilitychange", () => this.syncPlayback());
    }

    /* Open / close */

    open(index) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;

      // Close any open drawer (mobile menu, cart, search)
      document.body.dispatchEvent(new CustomEvent("overlay:close"));

      this.hidden = false;
      document.documentElement.classList.add("story-viewer-open");
      document.addEventListener("keydown", this.onKey);
      // Force a reflow so the fade-in runs, without waiting on a frame (paused in background tabs)
      void this.offsetWidth;
      this.setAttribute("data-open", "");

      this.userPaused = false;
      this.go(index || 0);
      this.querySelector(".story-viewer__control[data-story-close]").focus({ preventScroll: true });
      this.raf = requestAnimationFrame(this.tick);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      cancelAnimationFrame(this.raf);
      this.stopVideo(this.currentVideo());
      document.removeEventListener("keydown", this.onKey);
      this.removeAttribute("data-open");

      setTimeout(() => {
        if (this.isOpen) return;
        this.hidden = true;
        document.documentElement.classList.remove("story-viewer-open");
      }, 250);

      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    /* Navigation */

    go(index) {
      if (index >= this.slides.length) return this.close();
      index = Math.max(0, index);

      this.stopVideo(this.currentVideo());
      this.slides[this.index].removeAttribute("data-active");
      this.slides[this.index].setAttribute("aria-hidden", "true");
      this.setProductsFocusable(this.slides[this.index], false);

      this.index = index;
      this.elapsed = 0;
      this.lastTime = null;

      var slide = this.slides[index];
      slide.setAttribute("data-active", "");
      slide.setAttribute("aria-hidden", "false");
      this.setProductsFocusable(slide, true);

      this.bars.forEach((bar, i) => {
        bar.toggleAttribute("data-done", i < index);
        this.fills[i].style.transform = "";
      });

      this.prevButton.disabled = index === 0;
      this.muteButton.hidden = !this.currentVideo();

      // Warm up the next video, but only once this story is playing,
      // so it doesn't compete with the current download
      var next = this.slides[index + 1] && this.slides[index + 1].querySelector("video");
      var current = this.currentVideo();
      if (next && current && current.readyState < 3) {
        current.addEventListener("playing", () => (next.preload = "auto"), { once: true });
      } else if (next) {
        next.preload = "auto";
      }

      this.syncPlayback();
    }

    onKey(e) {
      if (e.key === "Escape") this.close();
      else if (e.key === "ArrowRight") this.go(this.index + 1);
      else if (e.key === "ArrowLeft") this.go(this.index - 1);
      else if (e.key === " " && !e.target.closest("a, button")) {
        e.preventDefault();
        this.userPaused = !this.userPaused;
        this.syncPlayback();
      }
    }

    /* Playback */

    currentVideo() {
      var slide = this.slides[this.index];
      return slide && slide.getAttribute("data-type") === "video" ? slide.querySelector("video") : null;
    }

    stopVideo(video) {
      if (!video) return;
      video.pause();
      video.currentTime = 0;
    }

    isPaused() {
      return this.userPaused || this.held || document.hidden || !this.isOpen;
    }

    syncPlayback() {
      var paused = this.isPaused();
      this.toggleAttribute("data-paused", this.userPaused);
      this.pauseButton.setAttribute("aria-label", this.userPaused ? "Play" : "Pause");

      var video = this.currentVideo();
      if (!video) return;
      video.muted = !this.sound;
      if (paused) {
        video.pause();
      } else {
        var play = video.play();
        // Autoplay blocked (e.g. low power mode): show the play button instead of stalling
        if (play && play.catch) {
          play.catch(() => {
            if (video !== this.currentVideo()) return;
            this.userPaused = true;
            this.syncPlayback();
          });
        }
      }
    }

    tick(time) {
      if (!this.isOpen) return;
      var dt = this.lastTime == null ? 0 : time - this.lastTime;
      this.lastTime = time;

      var progress;
      var video = this.currentVideo();
      if (video) {
        // Videos run for their full length; the bar follows the playhead
        progress = video.duration ? video.currentTime / video.duration : 0;
      } else {
        if (!this.isPaused()) this.elapsed += dt;
        progress = this.elapsed / this.imageDuration;
        if (progress >= 1) {
          this.go(this.index + 1);
          if (!this.isOpen) return;
          progress = 0;
        }
      }

      this.fills[this.index].style.transform = "scaleX(" + Math.min(progress, 1) + ")";
      this.raf = requestAnimationFrame(this.tick);
    }

    setProductsFocusable(slide, on) {
      slide.querySelectorAll(".story-product").forEach((a) => (a.tabIndex = on ? 0 : -1));
    }

    /* Tap left/right to navigate, hold to pause, swipe down to close */

    bindPointer() {
      var start = null;
      var holdTimer;

      var ignore = (e) => e.target.closest("button, a, [data-story-products]");

      this.frame.addEventListener("pointerdown", (e) => {
        if (ignore(e) || e.button > 0) return;
        start = { x: e.clientX, y: e.clientY };
        holdTimer = setTimeout(() => {
          this.held = true;
          this.setAttribute("data-holding", "");
          this.syncPlayback();
        }, 200);
      });

      var end = (e, cancelled) => {
        if (!start) return;
        clearTimeout(holdTimer);
        var dy = e.clientY - start.y;
        var dx = e.clientX - start.x;
        var wasHeld = this.held;
        start = null;

        if (wasHeld) {
          this.held = false;
          this.removeAttribute("data-holding");
          this.syncPlayback();
        }

        if (cancelled) return;
        if (dy > 80 && Math.abs(dy) > Math.abs(dx)) return this.close();
        if (wasHeld || Math.abs(dx) > 10 || Math.abs(dy) > 10) return;

        var rect = this.frame.getBoundingClientRect();
        if (e.clientX - rect.left < rect.width * 0.3) this.go(this.index - 1);
        else this.go(this.index + 1);
      };

      this.frame.addEventListener("pointerup", (e) => end(e, false));
      this.frame.addEventListener("pointercancel", (e) => end(e, true));
      // Long-press on iOS would otherwise open the image/video context menu
      this.frame.addEventListener("contextmenu", (e) => e.preventDefault());
    }

    /* Mouse drag for the product card row (touch scrolls natively) */

    bindProductDrag() {
      this.querySelectorAll("[data-story-products]").forEach((row) =>
        bindDragScroll(row, ".story-product", (on) => {
          this.held = on;
          this.syncPlayback();
        })
      );
    }
  }

  // Click-and-drag scrolling for a scroll-snap row with a mouse.
  // onHold(true/false) fires when a drag starts/ends; a drag never counts as a click.
  function bindDragScroll(row, itemSelector, onHold) {
    var start = null;
    var dragged = false;

    row.addEventListener("dragstart", (e) => e.preventDefault());

    row.addEventListener("pointerdown", (e) => {
      // A nested row (product cards inside a story row) handles the event first and claims it
      if (e.dragScrollClaimed || e.pointerType !== "mouse" || e.button > 0) return;
      if (row.scrollWidth <= row.clientWidth) return;
      e.dragScrollClaimed = true;
      start = { x: e.clientX, scroll: row.scrollLeft, id: e.pointerId };
      dragged = false;
    });

    row.addEventListener("pointermove", (e) => {
      if (!start) return;
      var dx = e.clientX - start.x;
      if (!dragged && Math.abs(dx) > 5) {
        dragged = true;
        row.setPointerCapture(start.id);
        row.classList.add("is-dragging");
        if (onHold) onHold(true);
      }
      if (dragged) row.scrollLeft = start.scroll - dx;
    });

    var end = () => {
      if (!start) return;
      start = null;
      if (!dragged) return;
      if (onHold) onHold(false);

      // Snap to the nearest item, then hand snapping back to CSS
      var items = Array.from(row.querySelectorAll(itemSelector));
      var padding = parseFloat(getComputedStyle(row).paddingLeft) || 0;
      var nearest = items.reduce((best, item) =>
        Math.abs(item.offsetLeft - padding - row.scrollLeft) <
        Math.abs(best.offsetLeft - padding - row.scrollLeft)
          ? item
          : best
      );
      row.scrollTo({ left: nearest.offsetLeft - padding, behavior: "smooth" });
      setTimeout(() => row.classList.remove("is-dragging"), 400);
    };

    row.addEventListener("pointerup", end);
    row.addEventListener("pointercancel", end);

    // A drag shouldn't open a link or count as a click
    row.addEventListener(
      "click",
      (e) => {
        if (!dragged) return;
        e.preventDefault();
        e.stopPropagation();
        dragged = false;
      },
      true
    );
  }

  customElements.define("story-viewer", StoryViewer);

  // Inline stories on the product page (sections/product-stories.liquid):
  // one highlighted story plays, the others wait dimmed beside it
  class ProductStories extends HTMLElement {
    connectedCallback() {
      this.row = this.querySelector("[data-pstories-row]");
      this.cards = Array.from(this.querySelectorAll("[data-pstory]"));
      if (!this.row || !this.cards.length) return;

      this.imageDuration = parseInt(this.getAttribute("data-image-duration"), 10) || 10000;
      this.index = -1;
      this.inView = false;
      this.sound = false;
      this.held = false;
      this.userPaused = false;
      this.tick = this.tick.bind(this);

      this.cards.forEach((card, i) => {
        var video = card.querySelector("video");
        if (video) {
          video.addEventListener("ended", () => {
            if (i === this.index) this.next();
          });
          video.addEventListener("error", () => card.setAttribute("data-type", "image"), true);
        }

        // A dimmed story becomes the highlighted one; the highlighted one toggles pause
        card.addEventListener("click", (e) => {
          if (e.target.closest("[data-story-products], button, a")) return;
          if (i !== this.index) return this.setActive(i, true);
          this.userPaused = !this.userPaused;
          this.sync();
        });
      });

      this.querySelectorAll("[data-pstories-mute]").forEach((button) =>
        button.addEventListener("click", () => {
          this.sound = !this.sound;
          this.toggleAttribute("data-sound", this.sound);
          this.querySelectorAll("[data-pstories-mute]").forEach((b) =>
            b.setAttribute("aria-label", this.sound ? "Mute" : "Unmute")
          );
          this.sync();
        })
      );

      var prev = this.querySelector("[data-pstories-prev]");
      var next = this.querySelector("[data-pstories-next]");
      if (prev) prev.addEventListener("click", () => this.setActive(Math.max(0, this.index - 1), true));
      if (next) next.addEventListener("click", () => this.next());

      // Swipe (touch) or drag (mouse) the row: the story passing into the slot becomes active
      // while the row is still moving, so a manual switch is as quick as an automatic one
      bindDragScroll(this.row, "[data-pstory]");
      var ticking = false;
      this.row.addEventListener(
        "scroll",
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            this.onScroll();
          });
        },
        { passive: true }
      );

      this.querySelectorAll("[data-story-products]").forEach((row) =>
        bindDragScroll(row, ".story-product", (on) => {
          this.held = on;
          this.sync();
        })
      );

      // Only play while the section is on screen
      new IntersectionObserver(
        (entries) => {
          this.inView = entries[0].isIntersecting;
          this.sync();
        },
        { threshold: 0.35 }
      ).observe(this.row);
      document.addEventListener("visibilitychange", () => this.sync());

      this.setActive(0, false);
    }

    videoOf(card) {
      return card && card.getAttribute("data-type") === "video" ? card.querySelector("video") : null;
    }

    padding() {
      return parseFloat(getComputedStyle(this.row).paddingLeft) || 0;
    }

    // Scroll position that puts a card in its slot: centered on mobile; on desktop the
    // second slot (the previous story stays visible on the left), except for the first story
    targetLeft(card) {
      if (getComputedStyle(card).scrollSnapAlign.indexOf("center") !== -1) {
        return card.offsetLeft - (this.row.clientWidth - card.offsetWidth) / 2;
      }
      var previous = this.cards[this.cards.indexOf(card) - 1];
      return (previous || card).offsetLeft - this.padding();
    }

    isFullyVisible(card) {
      var x = this.row.scrollLeft + this.padding();
      return card.offsetLeft >= x - 2 && card.offsetLeft + card.offsetWidth <= this.row.scrollLeft + this.row.clientWidth + 2;
    }

    setActive(i, scroll) {
      if (i === this.index) return;

      var previous = this.cards[this.index];
      if (previous) {
        previous.removeAttribute("data-active");
        var previousVideo = this.videoOf(previous);
        if (previousVideo) {
          previousVideo.pause();
          previousVideo.currentTime = 0;
        }
        previous.querySelector(".pstory__fill").style.transform = "";
      }

      this.index = i;
      this.elapsed = 0;
      this.userPaused = false;
      var card = this.cards[i];
      card.setAttribute("data-active", "");

      // Warm up the neighbours once this one is playing, so swiping either way starts quickly
      var neighbours = [this.videoOf(this.cards[i + 1]), this.videoOf(this.cards[i - 1])].filter(Boolean);
      var warmUp = () => neighbours.forEach((video) => (video.preload = "auto"));
      var current = this.videoOf(card);
      if (current && current.readyState < 3) {
        current.addEventListener("playing", warmUp, { once: true });
      } else {
        warmUp();
      }

      // Always move the highlighted story into its slot, so the row steps one story at a time
      if (scroll) {
        this.programmatic = true;
        this.row.scrollTo({ left: this.targetLeft(card), behavior: "smooth" });
        clearTimeout(this.programmaticTimer);
        this.programmaticTimer = setTimeout(() => (this.programmatic = false), 800);
      }

      this.sync();
    }

    next() {
      this.setActive((this.index + 1) % this.cards.length, true);
    }

    onScroll() {
      if (this.programmatic) return;
      // The story closest to its slot (second on desktop, center on mobile) becomes active
      var x = this.row.scrollLeft;
      var distance = (c) => Math.abs(this.targetLeft(c) - x);
      var nearest = 0;
      this.cards.forEach((c, i) => {
        if (distance(c) < distance(this.cards[nearest])) nearest = i;
      });
      this.setActive(nearest, false);
    }

    isPlaying() {
      return this.inView && !document.hidden && !this.userPaused && !this.held;
    }

    sync() {
      var card = this.cards[this.index];
      if (!card) return;
      var playing = this.isPlaying();
      this.toggleAttribute("data-paused", this.userPaused);

      var video = this.videoOf(card);
      if (video) {
        video.muted = !this.sound;
        if (playing) {
          var play = video.play();
          if (play && play.catch) {
            play.catch(() => {
              if (video !== this.videoOf(this.cards[this.index])) return;
              this.userPaused = true;
              this.toggleAttribute("data-paused", true);
            });
          }
        } else {
          video.pause();
        }
      }

      if (playing && !this.raf) {
        this.lastTime = null;
        this.raf = requestAnimationFrame(this.tick);
      }
    }

    tick(time) {
      this.raf = null;
      if (!this.isPlaying()) return;

      var dt = this.lastTime == null ? 0 : time - this.lastTime;
      this.lastTime = time;

      var card = this.cards[this.index];
      var video = this.videoOf(card);
      var progress;
      if (video) {
        progress = video.duration ? video.currentTime / video.duration : 0;
      } else {
        this.elapsed += dt;
        progress = this.elapsed / this.imageDuration;
        if (progress >= 1) {
          this.next();
          return;
        }
      }

      card.querySelector(".pstory__fill").style.transform = "scaleX(" + Math.min(progress, 1) + ")";
      this.raf = requestAnimationFrame(this.tick);
    }
  }

  customElements.define("product-stories", ProductStories);

  // Any link to #stories or [data-stories-open] opens the viewer
  // (optional data-stories-index to start at a given story)
  document.addEventListener(
    "click",
    (e) => {
      var trigger = e.target.closest("a[href$='#stories'], [data-stories-open]");
      var viewer = document.querySelector("story-viewer");
      if (!trigger || !viewer) return;
      e.preventDefault();
      e.stopPropagation();
      viewer.open(parseInt(trigger.getAttribute("data-stories-index"), 10) || 0);
    },
    true
  );

  if (location.hash === "#stories") {
    var openFromHash = () => {
      var viewer = document.querySelector("story-viewer");
      if (viewer) viewer.open(0);
    };
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", openFromHash);
    else openFromHash();
  }
}
